import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box, IconButton, Stack, SxProps, Theme} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

type HorizontalScrollerProps = {
    children: React.ReactNode,
    gap?: number,
    scrollButtons?: 'auto' | true | false,
    sx?: SxProps<Theme>,
};

export function HorizontalScroller({children, gap = 1, scrollButtons = 'auto', sx}: HorizontalScrollerProps) {
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const [scrollState, setScrollState] = useState({
        hasOverflow: false,
        canScrollLeft: false,
        canScrollRight: false,
    });

    const updateScrollState = useCallback(() => {
        const scroller = scrollerRef.current;
        if (!scroller) {
            return;
        }

        const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
        const nextScrollState = {
            hasOverflow: maxScrollLeft > 1,
            canScrollLeft: scroller.scrollLeft > 1,
            canScrollRight: scroller.scrollLeft < maxScrollLeft - 1,
        };

        setScrollState(current =>
            current.hasOverflow === nextScrollState.hasOverflow &&
            current.canScrollLeft === nextScrollState.canScrollLeft &&
            current.canScrollRight === nextScrollState.canScrollRight
                ? current
                : nextScrollState
        );
    }, []);

    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) {
            return;
        }

        updateScrollState();
        scroller.addEventListener('scroll', updateScrollState, {passive: true});

        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(scroller);

        if (scroller.firstElementChild) {
            resizeObserver.observe(scroller.firstElementChild);
        }

        return () => {
            scroller.removeEventListener('scroll', updateScrollState);
            resizeObserver.disconnect();
        };
    }, [children, updateScrollState]);

    const scroll = (direction: 'left' | 'right') => {
        const scroller = scrollerRef.current;
        if (!scroller) {
            return;
        }

        scroller.scrollBy({
            left: scroller.clientWidth * 0.8 * (direction === 'left' ? -1 : 1),
            behavior: 'smooth',
        });
    };

    const showScrollButtons = scrollButtons === true || (scrollButtons === 'auto' && scrollState.hasOverflow);

    return (
        <Stack direction="row" alignItems="center" gap={0.5} sx={{minWidth: 0, ...sx}}>
            {showScrollButtons && (
                <IconButton
                    aria-label="Scroll left"
                    title="Scroll left"
                    disabled={!scrollState.canScrollLeft}
                    sx={{visibility: scrollState.canScrollLeft || scrollButtons === true ? 'visible' : 'hidden'}}
                    onClick={() => scroll('left')}
                    size="small"
                >
                    <KeyboardArrowLeftIcon/>
                </IconButton>
            )}
            <Box
                ref={scrollerRef}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={gap}
                    sx={{
                        width: 'max-content',
                        minWidth: '100%',
                        '& > *': {
                            flexShrink: 0,
                        },
                    }}
                >
                    {children}
                </Stack>
            </Box>
            {showScrollButtons && (
                <IconButton
                    aria-label="Scroll right"
                    title="Scroll right"
                    disabled={!scrollState.canScrollRight}
                    sx={{visibility: scrollState.canScrollRight || scrollButtons === true ? 'visible' : 'hidden'}}
                    onClick={() => scroll('right')}
                    size="small"
                >
                    <KeyboardArrowRightIcon/>
                </IconButton>
            )}
        </Stack>
    );
}
